import { useState } from 'react';
import {
  Card, Table, Group, Text, Button, Select, SimpleGrid, Pagination, Box, Tooltip, Alert,
} from '@mantine/core';
import { MonthPickerInput } from '@mantine/dates';
import { useDebouncedValue } from '@mantine/hooks';
import {
  IconDownload, IconScale, IconCash, IconAlertTriangle, IconArrowUpRight, IconUsers,
} from '@tabler/icons-react';
import { useTranslation } from 'react-i18next';
import api from '../services/api';
import { usePagedQuery } from '../hooks/usePagedQuery';
import { useClasses } from '../hooks/useLookups';
import { EmptyState } from './EmptyState';
import { FetchOverlay } from './FetchOverlay';
import { StatCard } from './StatCard';
import { TableSkeleton } from './TableSkeleton';
import { TableToolbar } from './TableToolbar';
import { exportCsv } from '../lib/csv';
import { formatSom } from '../lib/format';
import { monthLabel, monthToDate, thisMonth, ym } from '../lib/month';
import {
  BALANCE_STATUSES, BALANCE_STATUS_COLOR, balanceStatusKey,
  type BalanceRow, type BalanceStatus,
} from '../lib/balance';
import { BalanceStatusBadge } from './BalanceStatusBadge';
import { SectionCard } from './SectionCard';

/**
 * Holat ro'yxati, ranglari va qator shakli — umumiy `lib/balance.ts` da
 * (o'quvchi/ota-ona kartochkalari ham AYNI xaritadan foydalanadi).
 */

/** Bir holat (yoki butun tanlov) bo'yicha yig'indi */
interface Totals { students: number; due: number; paid: number; debt: number; overpaid: number }

/** GET /reports/balances — sahifalangan konvert + taqsimotlar */
interface BalancesResp {
  month: string;
  /** Qarz hisobi boshlangan oy — undan oldingi oy uchun server bo'sh ro'yxat qaytaradi */
  debtStartMonth?: string | null;
  statuses: BalanceStatus[];
  byStatus: Record<BalanceStatus, Totals>;
  totals: Totals;
  filteredTotals: Totals;
}

const EMPTY_TOTALS: Totals = { students: 0, due: 0, paid: 0, debt: 0, overpaid: 0 };

/**
 * T10 — O'QUVCHILAR BALANSI (moliya sahifasining "Balanslar" ko'rinishi).
 *
 * Har bir faol o'quvchi uchun tanlangan OY kesimida: reja (oylik − chegirma),
 * shu oyga tushgan to'lovlar, qarz/ortiqcha va shundan kelib chiqadigan HOLAT.
 * Hisob-kitob butunlay backendda (`lib/balance.ts`) — qarz qiymati hisobotlar
 * sahifasidagi qarzdorlar jadvali bilan bir xil manbadan keladi.
 *
 * MUHIM UI qoidasi: holat chiplari `byStatus` dan chiziladi, u esa `status`
 * filtridan MUSTAQIL hisoblanadi — "Qarzdor" chipi bosilganda qolgan chiplar
 * nolga tushib, boshqa holatga o'tish imkoni yo'qolmaydi.
 */
export function BalancesPanel() {
  const { t } = useTranslation();
  const monthNames = t('monthsShort', { returnObjects: true }) as string[];
  const { data: classes } = useClasses();

  const [month, setMonth] = useState<string>(thisMonth());
  const [status, setStatus] = useState<BalanceStatus | null>(null);
  const [classF, setClassF] = useState<string>('');
  const [search, setSearch] = useState('');
  const [qDeb] = useDebouncedValue(search, 300);
  const [sort, setSort] = useState('debt:desc');
  const [exporting, setExporting] = useState(false);

  // Oy DOIM "YYYY-MM" ko'rinishida yuboriladi (T9) — backend "2026-5" ni ham
  // tushunadi, lekin panel bir xil formatni saqlaydi.
  const filters = {
    month,
    status: status || undefined,
    classId: classF || undefined,
    q: qDeb || undefined,
    sort,
  };

  const { items, isLoading, fetching, pages, page, setPage, data, total } =
    usePagedQuery<BalanceRow>('balances', '/reports/balances', filters, 20);
  const meta = data as unknown as BalancesResp | undefined;
  const byStatus = meta?.byStatus;
  const totals = meta?.totals ?? EMPTY_TOTALS;
  const filtered = meta?.filteredTotals ?? EMPTY_TOTALS;
  const statuses = meta?.statuses ?? BALANCE_STATUSES;

  const statusLabel = (s: BalanceStatus) => t(balanceStatusKey(s));

  /** Chipdagi pul qiymati — holatning MA'NOSIGA mos ustun. */
  const statusMoney = (s: BalanceStatus, v: Totals) =>
    s === 'OVERPAID' ? v.overpaid : s === 'PAID' ? v.paid : v.debt;

  const exportRows = async () => {
    setExporting(true);
    try {
      // Joriy SAHIFA emas, filtrlangan TO'LIQ ro'yxat (`all=1`) — hisobotlar
      // sahifasidagi qarzdorlar eksporti bilan bir xil naqsh.
      const { data: full } = await api.get<{ month: string; items: BalanceRow[] }>('/reports/balances', {
        params: { ...filters, all: 1 },
      });
      exportCsv(
        `balanslar-${full.month}`,
        full.items.map((r) => ({
          [t('att.student')]: r.fullName,
          [t('reports.class')]: r.className ?? '—',
          [t('balances.discount')]: r.discount,
          [t('reports.plan')]: r.due,
          [t('reports.paidCol')]: r.paid,
          [t('reports.debtCol')]: r.debt,
          [t('balances.overpaid')]: r.overpaid,
          [t('balances.status')]: statusLabel(r.status),
        })),
      );
    } finally {
      setExporting(false);
    }
  };

  // Tanlangan oy qarz hisobidan oldin — jadval bo'sh, sababini aytamiz (aks holda
  // "hamma to'lagan" yoki "ma'lumot yo'qolgan" deb tushunilardi)
  const hisobdanOldin = !!meta?.debtStartMonth && month < meta.debtStartMonth;

  return (
    <>
      {hisobdanOldin && (
        <Alert color="gray" variant="light" mb="md">
          {t('debtStart.beforeNotice', { month: monthLabel(meta!.debtStartMonth!, monthNames) })}
        </Alert>
      )}
      <SimpleGrid cols={{ base: 2, md: 4 }} mb="md">
        <StatCard icon={IconScale} color="brand" label={t('balances.due')} value={formatSom(totals.due)}
          hint={t('common.itemsCount', { count: totals.students })} />
        <StatCard icon={IconCash} color="green" label={t('reports.paidCol')} value={formatSom(totals.paid)} />
        <StatCard icon={IconAlertTriangle} color="red" label={t('reports.debtCol')} value={formatSom(totals.debt)} />
        <StatCard icon={IconArrowUpRight} color="blue" label={t('balances.overpaid')} value={formatSom(totals.overpaid)} />
      </SimpleGrid>

      {/* Holat chiplari — bosilganda filtr yoqiladi/o'chiriladi (ikkinchi bosish tozalaydi) */}
      <Card mb="md">
        <Group justify="space-between" mb="sm" wrap="nowrap">
          <Group gap={8} wrap="nowrap" align="baseline">
            <Text fw={700}>{t('balances.byStatus')}</Text>
            <Text fz="xs" c="dimmed">{monthLabel(meta?.month ?? month, monthNames)}</Text>
          </Group>
          <Text fz="xs" c="dimmed">{t('balances.clickHint')}</Text>
        </Group>
        <Group gap="sm">
          {statuses.map((s) => {
            const v = byStatus?.[s] ?? EMPTY_TOTALS;
            const active = status === s;
            return (
              <Button
                key={s}
                variant={active ? 'filled' : 'light'}
                color={BALANCE_STATUS_COLOR[s]}
                radius="xl"
                size="compact-md"
                onClick={() => setStatus(active ? null : s)}
                leftSection={<IconUsers size={14} />}
              >
                {statusLabel(s)}: {v.students} · {formatSom(statusMoney(s, v))}
              </Button>
            );
          })}
        </Group>
      </Card>

      <TableToolbar
        search={search}
        onSearch={setSearch}
        searchPlaceholder={t('balances.searchPlaceholder')}
        sort={sort}
        onSort={setSort}
        sortOptions={[
          { value: 'debt:desc', label: t('reports.sortDebtDesc') },
          { value: 'debt:asc', label: t('reports.sortDebtAsc') },
          { value: 'overpaid:desc', label: t('balances.sortOverpaidDesc') },
          { value: 'fullName:asc', label: t('reports.sortNameAsc') },
          { value: 'className:asc', label: t('reports.sortClassAsc') },
          { value: 'due:desc', label: t('reports.sortDueDesc') },
          { value: 'paid:desc', label: t('reports.sortPaidDesc') },
        ]}
      >
        <MonthPickerInput
          label={t('fields.month')}
          value={monthToDate(month)}
          onChange={(d) => setMonth(d ? ym(d) : thisMonth())}
          valueFormat="YYYY MMMM"
          w={170}
        />
        <Select
          placeholder={t('fields.class')}
          data={(classes ?? []).map((c) => ({ value: c.id, label: c.name }))}
          value={classF || null}
          onChange={(v) => setClassF(v ?? '')}
          clearable searchable w={150}
        />
        <Select
          placeholder={t('balances.allStatuses')}
          data={statuses.map((s) => ({ value: s, label: statusLabel(s) }))}
          value={status}
          onChange={(v) => setStatus((v as BalanceStatus) || null)}
          clearable w={180}
        />
        <Button
          size="sm"
          variant="light"
          leftSection={<IconDownload size={14} />}
          loading={exporting}
          disabled={!total}
          onClick={exportRows}
        >
          Excel
        </Button>
      </TableToolbar>

      {/* Holat tanlanganda jadval yig'indisi kartalardan farq qiladi — ochiq yozamiz */}
      {status && (
        <Text fz="xs" c="dimmed" mb="xs">
          {t('balances.filteredSummary', {
            status: statusLabel(status),
            count: filtered.students,
            debt: formatSom(filtered.debt),
            paid: formatSom(filtered.paid),
          })}
        </Text>
      )}

      <SectionCard title={t('common.list')} icon={IconScale} p={0} pos="relative">
        {isLoading ? (
          <TableSkeleton rows={6} cols={8} />
        ) : !items.length ? (
          <EmptyState icon={IconScale} title={t('balances.empty')} description={t('common.noMatch')} />
        ) : (
          <Table.ScrollContainer minWidth={960}>
            <FetchOverlay visible={fetching} />
            <Table highlightOnHover verticalSpacing="sm" stickyHeader>
              <Table.Thead>
                <Table.Tr>
                  <Table.Th>{t('att.student')}</Table.Th>
                  <Table.Th>{t('reports.class')}</Table.Th>
                  {/* Chegirma `due` ichida jim ayriladi — alohida ustunsiz "reja nega
                      kichik" degan savol javobsiz qolardi (#66) */}
                  <Table.Th ta="right">
                    <Tooltip label={t('balances.discountHint')} withArrow>
                      <Text component="span" fz="sm" fw={600} style={{ borderBottom: '1px dotted currentColor' }}>
                        {t('balances.discount')}
                      </Text>
                    </Tooltip>
                  </Table.Th>
                  <Table.Th ta="right">{t('reports.plan')}</Table.Th>
                  <Table.Th ta="right">{t('reports.paidCol')}</Table.Th>
                  <Table.Th ta="right">{t('reports.debtCol')}</Table.Th>
                  <Table.Th ta="right">{t('balances.overpaid')}</Table.Th>
                  <Table.Th>{t('balances.status')}</Table.Th>
                </Table.Tr>
              </Table.Thead>
              <Table.Tbody>
                {items.map((r) => (
                  <Table.Tr key={r.studentId}>
                    <Table.Td fw={500}>{r.fullName}</Table.Td>
                    <Table.Td>{r.className ?? '—'}</Table.Td>
                    <Table.Td ta="right">
                      {r.discount > 0 ? <Text c="orange.7" fw={600}>−{formatSom(r.discount)}</Text> : <Text c="dimmed">—</Text>}
                    </Table.Td>
                    <Table.Td ta="right">
                      {formatSom(r.due)}
                      {/* Reja yo'q, lekin to'lov bor — buni aytmasak "ortiqcha to'lagan"
                          degan holat sababsiz ko'ringan bo'lardi */}
                      {!r.hasPlan && <Text fz="xs" c="dimmed">{t('balances.noPlan')}</Text>}
                    </Table.Td>
                    <Table.Td ta="right" c="green.7" fw={600}>{formatSom(r.paid)}</Table.Td>
                    <Table.Td ta="right">
                      {r.debt > 0 ? <Text fw={700} c="red.7">{formatSom(r.debt)}</Text> : <Text c="dimmed">—</Text>}
                    </Table.Td>
                    <Table.Td ta="right">
                      {r.overpaid > 0 ? <Text fw={600} c="blue.7">{formatSom(r.overpaid)}</Text> : <Text c="dimmed">—</Text>}
                    </Table.Td>
                    <Table.Td>
                      <Tooltip label={t('balances.balanceHint', { value: formatSom(r.balance) })} withArrow>
                        <BalanceStatusBadge status={r.status} />
                      </Tooltip>
                    </Table.Td>
                  </Table.Tr>
                ))}
              </Table.Tbody>
            </Table>
          </Table.ScrollContainer>
        )}
      </SectionCard>

      {pages > 1 && (
        <Box mt="md">
          <Group justify="center">
            <Pagination value={page} onChange={setPage} total={pages} />
          </Group>
        </Box>
      )}
    </>
  );
}
